import { readFile, type Vfs } from "idfweb-vfs";

import { GUEST_IDF } from "./toolchain.ts";

/**
 * The chip targets of the bundled ESP-IDF, read from its checkout.
 *
 * `tools/idf_py_actions/constants.py` holds the two lists idf.py uses,
 * `SUPPORTED_TARGETS` and `PREVIEW_TARGETS`. They are parsed as text, since
 * no Python runs here. `linux` is a preview target for host builds and is
 * dropped.
 */
export interface IdfTargets {
    supported: string[];
    preview: string[];
}

export const IDF_TARGETS_FILE = `${GUEST_IDF}/tools/idf_py_actions/constants.py`;

const EXCLUDED = new Set(["linux"]);

export function readIdfTargets(vfs: Vfs): IdfTargets {
    return parseIdfTargets(new TextDecoder().decode(readFile(vfs, IDF_TARGETS_FILE)));
}

/** Parse the two list assignments out of the Python source. */
export function parseIdfTargets(source: string): IdfTargets {
    return {
        supported: pythonStringList(source, "SUPPORTED_TARGETS"),
        preview: pythonStringList(source, "PREVIEW_TARGETS").filter((t) => !EXCLUDED.has(t)),
    };
}

export function isIdfTarget(targets: IdfTargets, name: string): boolean {
    return targets.supported.includes(name) || targets.preview.includes(name);
}

/** The elements of `NAME = ['a', 'b', ...]`, where every element is a quoted string. */
function pythonStringList(source: string, name: string): string[] {
    const match = new RegExp(`^${name}\\s*=\\s*\\[([^\\]]*)\\]`, "m").exec(source);
    if (match === null) throw new Error(`${IDF_TARGETS_FILE}: no ${name} list`);
    return [...match[1]!.matchAll(/(['"])([^'"]*)\1/g)].map((m) => m[2]!);
}
